/**
 * **What the visitor sent with a booking**, on the booking's record (C25,
 * FR-J11, FR-F3;
 * [ADR-0053](../../../../../../docs/adr/0053-a-booking-may-ask-for-documents-and-a-visitor-may-upload-them.md)
 * §5; Wave 27C, T-5.76). One `RecordSection` at the end of `Detail.tsx`.
 *
 * Read from `GET /api/v1/documents?booking_id=` — the documents themselves,
 * not a copy on the booking — so each is opened the way every document is
 * (a five-minute link, the read recorded: constitution §4), carries the
 * wrong-client guardrail's words as a staff upload does (FR-E14; run when the
 * booking was submitted), and is gated as documents are: `document:can_view`,
 * and FR-L4, under which a booking's documents with no case are the client's
 * and a narrow reader does not see them.
 *
 * Until the booking is submitted, what the visitor sent is **held**, not filed
 * (ADR-0053 §3) — they may still remove it — so a booking still being made
 * lists nothing here and says why. The same documents are in the client's
 * documents, marked *from a booking*, and on the case when the flow opens one;
 * they are **not** in the client portal until staff share one (§9-90).
 */

import { Button } from '@fiveox/ui'
import { AlertTriangle, FileText } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import { RecordSection } from '../../../components/page'
import { DOCUMENT, useEvaluation } from '../../../lib/access'
import { ApiError } from '../../../lib/api'
import type { BookingStatus } from '../../../lib/api/booking'
import { type DocumentRow, documents, fileSize } from '../../../lib/api/documents'

/** The line under a document's name: its file, its size and its kind. */
export function documentLine(row: Pick<DocumentRow, 'filename' | 'size_bytes' | 'content_type'>): string {
  const kind = row.content_type === 'application/pdf' ? 'PDF' : row.content_type.startsWith('image/') ? 'Photo' : null
  return [row.filename, fileSize(row.size_bytes), kind].filter(Boolean).join(' · ')
}

/** What the section says when there is nothing to list. */
export function noDocumentsText(status: BookingStatus | null | undefined): string {
  return status === 'started'
    ? 'Anything they send is filed here when they finish the booking — until then they may still remove it.'
    : 'They sent no documents with this booking.'
}

export function BookingDocuments({ bookingId, status }: { bookingId: string; status?: BookingStatus | null }) {
  const { can } = useEvaluation()
  const allowed = can(DOCUMENT, 'can_view')
  const [rows, setRows] = useState<DocumentRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [opening, setOpening] = useState<string | null>(null)
  const [openError, setOpenError] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!allowed) return
    let live = true
    setRows(null)
    setError(null)
    documents
      .list({ booking_id: bookingId, limit: 50 })
      .then((body) => live && setRows(body.data))
      .catch((err: unknown) => {
        if (!live) return
        setError(err instanceof ApiError ? err.message : 'The documents could not be loaded. Reload the page to try again.')
      })
    return () => {
      live = false
    }
  }, [bookingId, allowed])

  async function open(row: DocumentRow) {
    setOpening(row.id)
    setOpenError((prev) => ({ ...prev, [row.id]: '' }))
    try {
      const body = await documents.downloadUrl(row.id, 'view')
      // A five-minute capability. Used and dropped — never stored.
      window.open(body.data.url, '_blank', 'noopener')
    } catch (err) {
      setOpenError((prev) => ({
        ...prev,
        [row.id]: err instanceof ApiError ? err.message : 'That document could not be opened. Try again.',
      }))
    } finally {
      setOpening(null)
    }
  }

  return (
    <RecordSection
      id="booking-documents"
      icon={<FileText size={18} aria-hidden />}
      title="Documents"
      description="What they sent with the booking. Filed in the client’s documents, and on the case when one opens."
    >
      <div data-booking-documents="">
        {!allowed ? (
          <p className="text-muted-foreground text-sm">Your role cannot open documents.</p>
        ) : error ? (
          <p className="text-destructive text-sm">{error}</p>
        ) : rows === null ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-sm" data-booking-documents-none="">
            {noDocumentsText(status)}
          </p>
        ) : (
          <ul className="divide-border divide-y text-sm">
            {rows.map((row) => (
              <li key={row.id} className="space-y-0.5 py-2.5 first:pt-0 last:pb-0" data-booking-document={row.id}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <Button variant="link" onClick={() => void open(row)} disabled={opening === row.id}>
                    <span className="break-all text-left">{row.title || row.filename}</span>
                  </Button>
                  {row.case_id && (
                    <Link to={`/cases/${row.case_id}/documents`} className="text-primary text-xs font-medium hover:underline">
                      On the case
                    </Link>
                  )}
                </div>
                <p className="text-muted-foreground text-xs">{documentLine(row)}</p>
                {row.guardrail?.warnings?.length ? (
                  <p className="text-muted-foreground text-xs" data-guardrail="">
                    <AlertTriangle size={12} className="mr-1 inline" aria-hidden />
                    {row.guardrail.warnings.map((w) => w.message).join(' ')}
                  </p>
                ) : null}
                {openError[row.id] && <p className="text-destructive text-xs">{openError[row.id]}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </RecordSection>
  )
}
